import React, { useEffect, useState } from "react";
import { RootState } from "us.helper/types";
import { ConnectedProps, connect } from "react-redux";
import { useTranslation } from "react-i18next";
import * as DOMPurify from "dompurify";

import { $Button, $MessageBox } from "us.common/components";
import * as Actions from "actions";
import "./RichTextEditor.scss";
import {
  getSectionByName,
  getSectionRuleByRule,
  updateTemplateSectionRule,
} from "functions";
import { ISectionRule } from "interfaces";
import { Parameters } from "components";
import {
  adjustContent,
  generateSectionContents,
  positioningWithRootDiv,
} from "components/TemplateEditor/Functions";
import { RTE_CONFIG } from "constants/RTEditor";
import { handleTableCellsBorderStyling } from "./functions";

type RichTextEditorProps = {
  onClose: () => void;
};

const RichTextEditor: React.FC<RichTextEditorProps & PropsFromRedux> = ({
  section,
  drawerInfo,
  template,
  updateTemplate,
  onClose,
}) => {
  const { t } = useTranslation();
  const [richTextEditor, setRichTextEditor] = useState<any>(null);

  useEffect(() => {
    if (typeof window.initRTEditor == "function" && !richTextEditor) {
      const editor: any = window.initRTEditor("rte_content", RTE_CONFIG);
      setRichTextEditor(editor);
      if (editor != null) {
        editor.setHTMLCode(drawerInfo.content);
        editor.commitBookmark();
        editor.focus();
        editor.attachEvent(
          "exec_command_bordernone",
          function (state: { returnValue: boolean }, cmd: any, value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("bordernone", editor);
          }
        );
        editor.attachEvent(
          "exec_command_bordertop",
          function (state: { returnValue: boolean }, cmd: any, value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("bordertop", editor);
          }
        );
        editor.attachEvent(
          "exec_command_borderbottom",
          function (state: { returnValue: boolean }, cmd: any, value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderbottom", editor);
          }
        );
        editor.attachEvent(
          "exec_command_borderhorizontal",
          function (state: { returnValue: boolean }, cmd: any, value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderhorizontal", editor);
          }
        );
        editor.attachEvent(
          "exec_command_borderall",
          function (state: { returnValue: boolean }, cmd: any, value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderall", editor);
          }
        );
        editor.attachEvent(
          "exec_command_borderdashed",
          function (state: { returnValue: boolean }, cmd: any, value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderstyle", editor, "dashed");
          }
        );
        editor.attachEvent(
          "exec_command_borderdotted",
          function (state: { returnValue: boolean }, cmd: any, value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderstyle", editor, "dotted");
          }
        );
        editor.attachEvent(
          "exec_command_bordersolid",
          function (state: { returnValue: boolean }, cmd: any, value: any) {
            state.returnValue = true; //set it has been handled
            handleTableCellsBorderStyling("borderstyle", editor);
          }
        );
        // attached an event to sanitize the paste content from Word
        editor.attachEvent("change", function () {
          var content = editor.getHTMLCode();
          const adjustedContent = adjustContent(content);
          if (adjustedContent.isModified) {
            editor.setHTMLCode(adjustedContent.content);
            editor.commitBookmark();
          }
          if (content.includes("xml") && content.includes("Microsoft Word")) {
            const config = {
              FORBID_TAGS: ["style", "xml", "meta"],
              KEEP_CONTENT: false,
              ADD_TAGS: ["params"],
              ADD_ATTR: ["contenteditable"],
            };
            const cleanedContent = DOMPurify.sanitize(content, config);
            editor.setHTMLCode(cleanedContent);
            editor.commitBookmark();
          }
        });
      }
    }
  }, []);

  /**
   * @description - Handle save the changes to selected section
   */
  const handleSave = () => {
    try {
      // get the updated html content from the editor
      const htmlCode = richTextEditor.getHTMLCode();
      const updatedHtmlContent = positioningWithRootDiv(
        drawerInfo.content,
        htmlCode
      );
      if (updatedHtmlContent) {
        const oldSection = getSectionByName(
          section.name,
          template.data.templateContent.pages
        );
        if (oldSection) {
          const oldSectionRule = getSectionRuleByRule(
            section.selectedRule,
            oldSection
          );
          if (oldSectionRule) {
            const sectionContents = generateSectionContents(
              oldSectionRule,
              updatedHtmlContent
            );
            const updatedSectionRule: ISectionRule = {
              ...oldSectionRule,
              content: sectionContents.content ?? oldSectionRule.content,
              repeatingContent: sectionContents.repeatingContent,
              isRepeating: Boolean(sectionContents.isRepeating),
              repeatingAdapterId: parseInt(
                sectionContents.repeatingAdapterId ?? "0"
              ),
            };
            const updatedTemplate = updateTemplateSectionRule(
              section.name,
              section.selectedRule,
              updatedSectionRule,
              template.data
            );
            updateTemplate && updateTemplate(updatedTemplate);
          }
        }
      }
    } catch (error) {
      console.error("Error saving the Section Editor", error);
      $MessageBox(
        "error",
        `US.USC.TEMPLATEEDITOR:MESSAGES.TEMPLATE_SECTION_UPDATE_FAILED`,
        "",
        ""
      );
    } finally {
      onClose();
      richTextEditor.collapse(true);
    }
  };

  const getSelectionParentElement = (selection: any) => {
    var parentEl = null,
      sel;
    if (selection) {
      sel = selection;
      if (sel?.rangeCount) {
        parentEl = sel.getRangeAt(0).commonAncestorContainer;
        if (parentEl.nodeType != 1) {
          parentEl = parentEl.parentNode;
        }
      }
    }
    return parentEl;
  };

  /**
   * @description - Handle parameter binding with section by double clicking the param, if it's a normal table it will convert to a
   * repeating table if user add a repeating param to a table row
   * @param {string} tokenName - Token name
   * @param {string} isRepeating - Token is a repeating token or not
   * @param {string} adapterId - Adapter id
   */
  const handleParamsBinding = (
    tokenName: string,
    isRepeating: boolean,
    adapterId?: number
  ) => {
    try {
      if (richTextEditor && typeof richTextEditor.insertHTML == "function") {
        if (isRepeating) {
          const parentTd = getSelectionParentElement(
            richTextEditor.getSelection()
          );
          if (parentTd) {
            const repeatingRow = parentTd.closest("tr");
            const tableElement = parentTd.closest("table");
            if (!tableElement?.hasAttribute("id")) {
              tableElement?.setAttribute("id", "repeatableTable");
            }
            if (!tableElement?.hasAttribute("repeatable")) {
              tableElement?.setAttribute("repeatable", "true");
            }
            if (
              !tableElement?.hasAttribute("repeatingadapterid") &&
              tableElement?.getAttribute("repeatingadapterid") != "0"
            ) {
              tableElement?.setAttribute("repeatingadapterid", `${adapterId}`);
            }

            if (repeatingRow && !repeatingRow.hasAttribute("id")) {
              repeatingRow.setAttribute("id", "repeatingcontent");
            }
            repeatingRow.setAttribute("repeatingadapterid", `${adapterId}`);
          }
        }
        richTextEditor.insertHTML(
          `<params contenteditable="false" suppresscontenteditablewarning="true" isRepeating=${isRepeating}>${tokenName}</params>`
        );
        richTextEditor.collapse(false);
      }
    } catch (error) {
      console.error("Error in handleParamsBinding function : ", error);
    }
  };

  return (
    <>
      <div className="section-editor-layout">
        <div className="section-editor-layout-content">
          <div id="rte_content" />
        </div>
        <div className="section-editor-layout-side">
          <Parameters
            disabledAddRepeatingAdapter={true}
            onDoubleClick={handleParamsBinding}
          />
        </div>
      </div>
      <div className="drawer-footer-fixed align-content-center justify-content-end">
        <div>
          <$Button className="" onClick={handleSave} type="primary">
            {t("US.COLLECTION.COMMON:COMMON.SAVE")}
          </$Button>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templateEditor, templatesHistory } = state;
  const { parameters, templateRules, section, drawerInfo } = templateEditor;
  const { template } = templatesHistory;

  return {
    tokens: parameters.standard.dataTokens,
    templateRules,
    template,
    section,
    drawerInfo,
  };
};

const { section } = Actions.templateEditor;
const { template } = Actions.templatesHistory;

const mapDispatchToProps = {
  selectSection: section.selectSection,
  updateTemplate: template.update,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(RichTextEditor);
